import React, { useRef, useEffect } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AgentChatMessageBubble from "../common/AgentChatMessageBubble";
import ContextWindowMeter from "../common/ContextWindowMeter";
import type { IntegrationSupportChatMessage } from "../../hooks/useIntegrationSupportAgentChat";
import type { AgentContextUsage } from "../../types/agentChat";
import { HUB_CHAT_PANEL_HEIGHT } from "../../utils/hubLayout";

interface IntegrationSupportChatPanelProps {
  messages: IntegrationSupportChatMessage[];
  onSend: (text: string) => void;
  loading: boolean;
  error: string | null;
  contextUsage?: AgentContextUsage | null;
  quickPrompts?: string[];
  height?: number | string | typeof HUB_CHAT_PANEL_HEIGHT;
  placeholder?: string;
  subtitle?: string;
}

const IntegrationSupportChatPanel: React.FC<IntegrationSupportChatPanelProps> = ({
  messages,
  onSend,
  loading,
  error,
  contextUsage = null,
  quickPrompts = [],
  height = HUB_CHAT_PANEL_HEIGHT,
  placeholder = "Ask about integration, troubleshooting, or paste a question…",
  subtitle = "Integration support chat",
}) => {
  const [input, setInput] = React.useState("");
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input);
    setInput("");
  };

  return (
    <Paper variant="outlined" sx={{ display: "flex", flexDirection: "column", height }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          {subtitle}
        </Typography>
        <ContextWindowMeter contextUsage={contextUsage} />
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2, minHeight: 0 }}>
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              mb: 1.5,
              display: "flex",
              justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            }}
          >
            <AgentChatMessageBubble
              role={m.role}
              content={m.content}
              structuredBlockPreset={
                m.role === "assistant" ? "integration" : undefined
              }
            />
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <div ref={endRef} />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}
      {quickPrompts.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {quickPrompts.map((p) => (
            <Typography
              key={p}
              variant="caption"
              component="button"
              onClick={() => onSend(p)}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1,
                py: 0.25,
                cursor: "pointer",
                bgcolor: "background.paper",
              }}
            >
              {p}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          disabled={loading}
          multiline
          maxRows={4}
        />
        <IconButton color="primary" onClick={handleSend} disabled={loading || !input.trim()}>
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default IntegrationSupportChatPanel;
